import { Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import {
  useDeleteProductLogo,
  useProductBranding,
  useUploadProductLogo,
} from '../../../../../shared/hooks/useProductBranding';

const MAX_BYTES = 1 * 1024 * 1024;
const ACCEPTED_MIMES = new Set(['image/png', 'image/x-png', 'image/svg+xml', 'image/svg']);

export interface LogoUploaderProps {
  productId: string | null;
}

/**
 * Iteration-4 product logo upload UI. Mirrors the Iteration-0 server contract:
 *   - PNG / SVG only, ≤1 MB
 *   - SVG is sanitised server-side; client just nudges the user toward valid
 *     types and surfaces backend error reasons inline.
 *   - Existing logo URL is rendered as a preview with a Remove button.
 */
export const LogoUploader: React.FC<LogoUploaderProps> = ({ productId }) => {
  const branding = useProductBranding(productId);
  const upload = useUploadProductLogo();
  const remove = useDeleteProductLogo();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasLogo = !!branding.data?.logoUrl;

  const validateAndUpload = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!productId) return;
      if (!ACCEPTED_MIMES.has(file.type)) {
        setError(`Unsupported file type: ${file.type || 'unknown'}. Use PNG or SVG.`);
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(`File is too large: ${(file.size / 1024).toFixed(0)} KB (max 1 MB).`);
        return;
      }
      setError(null);
      upload.mutate(
        { productId, file },
        {
          onError: (err) => setError(err.message || 'Upload failed'),
        }
      );
    },
    [productId, upload]
  );

  return (
    <div className="rb-logo-uploader">
      <div
        className={`rb-logo-uploader__zone${dragOver ? ' rb-logo-uploader__zone--drag' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          validateAndUpload(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Upload product logo"
      >
        {hasLogo ? (
          <img
            src={branding.data!.logoUrl as string}
            alt="Current product logo"
            className="rb-logo-uploader__current"
          />
        ) : (
          <div className="rb-logo-uploader__placeholder">
            <ImageIcon className="w-6 h-6" aria-hidden="true" />
            <span>Drag &amp; drop or click to upload</span>
            <span className="rb-logo-uploader__hint">PNG or SVG · ≤1 MB</span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/svg+xml"
        className="rb-logo-uploader__input"
        onChange={(e) => validateAndUpload(e.target.files?.[0])}
      />
      <div className="rb-logo-uploader__actions">
        <button
          type="button"
          className="rb-logo-uploader__btn"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending || !productId}
        >
          <Upload className="w-4 h-4" aria-hidden="true" />
          <span>{hasLogo ? 'Replace' : 'Upload'}</span>
        </button>
        {hasLogo ? (
          <button
            type="button"
            className="rb-logo-uploader__btn rb-logo-uploader__btn--danger"
            onClick={() => {
              if (productId) remove.mutate({ productId });
            }}
            disabled={remove.isPending}
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            <span>Remove</span>
          </button>
        ) : null}
      </div>
      {error ? <p className="rb-logo-uploader__error">{error}</p> : null}
      {upload.isPending ? <p className="rb-logo-uploader__hint">Uploading…</p> : null}
    </div>
  );
};
